import React, { useState, useCallback } from 'react';
import { parse, isValid } from 'date-fns';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import T from 'i18n';
import type { CommentsFilter } from './CommentsPanel';

type FilterCommentsPanelProps = {
  commentsFilter: CommentsFilter;
  setCommentsFilter: React.Dispatch<React.SetStateAction<CommentsFilter>>;
  hideMyAnnotations: boolean;
  hideOthersAnnotations: boolean;
  onHideMyAnnotationsChange: (value: boolean) => void;
  onHideOthersAnnotationsChange: (value: boolean) => void;
};

type Period = 'AM' | 'PM';

const DATE_FORMAT = 'dd/MM/yyyy';
const TIME_FORMAT = 'hh:mm';
const FULL_FORMAT = 'dd/MM/yyyy hh:mm aa';

const formatDateValue = (date: Date | null): string => {
  if (!date || !isValid(date)) return '';
  const d = date.getDate().toString().padStart(2, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const y = date.getFullYear().toString();
  return `${d}/${m}/${y}`;
};

const formatTimeOnly = (date: Date | null): string => {
  if (!date || !isValid(date)) return '';
  let h = date.getHours() % 12 || 12;
  const min = date.getMinutes().toString().padStart(2, '0');
  return `${h.toString().padStart(2, '0')}:${min}`;
};

const getPeriod = (date: Date | null): Period => {
  if (!date || !isValid(date)) return 'AM';
  return date.getHours() >= 12 ? 'PM' : 'AM';
};

const parseDateTime = (dateStr: string, timeStr: string, period: Period): Date | null => {
  const trimmedDate = dateStr.trim();
  const trimmedTime = timeStr.trim();

  if (!trimmedDate && !trimmedTime) return null;

  const datePart = trimmedDate || '01/01/2000';
  const timePart = trimmedTime || '12:00';

  const parsed = parse(`${datePart} ${timePart} ${period}`, FULL_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
};

const isFieldValid = (value: string, format: string): boolean => {
  if (!value.trim()) return true;
  return isValid(parse(value.trim(), format, new Date()));
};

const FilterCommentsPanel: React.FC<FilterCommentsPanelProps> = ({
  commentsFilter,
  setCommentsFilter,
  hideMyAnnotations,
  hideOthersAnnotations,
  onHideMyAnnotationsChange,
  onHideOthersAnnotationsChange,
}) => {
  const [fromDate, setFromDate] = useState(() => formatDateValue(commentsFilter.from));
  const [fromTime, setFromTime] = useState(() => formatTimeOnly(commentsFilter.from));
  const [fromPeriod, setFromPeriod] = useState<Period>(() => getPeriod(commentsFilter.from));
  const [toDate, setToDate] = useState(() => formatDateValue(commentsFilter.to));
  const [toTime, setToTime] = useState(() => formatTimeOnly(commentsFilter.to));
  const [toPeriod, setToPeriod] = useState<Period>(() => getPeriod(commentsFilter.to));

  const fromDateValid = isFieldValid(fromDate, DATE_FORMAT);
  const fromTimeValid = isFieldValid(fromTime, TIME_FORMAT);
  const toDateValid = isFieldValid(toDate, DATE_FORMAT);
  const toTimeValid = isFieldValid(toTime, TIME_FORMAT);

  const allValid = fromDateValid && fromTimeValid && toDateValid && toTimeValid;

  const hasFromInput = fromDate.trim() !== '' || fromTime.trim() !== '';
  const hasToInput = toDate.trim() !== '' || toTime.trim() !== '';
  const canSubmit = allValid && (hasFromInput || hasToInput);

  const handleApply = useCallback(() => {
    if (!allValid) return;

    const from = hasFromInput ? parseDateTime(fromDate, fromTime, fromPeriod) : null;
    const to = hasToInput ? parseDateTime(toDate, toTime, toPeriod) : null;

    if ((hasFromInput && !from) || (hasToInput && !to)) return;

    setCommentsFilter({ from, to });
  }, [
    allValid,
    fromDate,
    fromTime,
    fromPeriod,
    toDate,
    toTime,
    toPeriod,
    hasFromInput,
    hasToInput,
    setCommentsFilter,
  ]);

  const handleClear = useCallback(() => {
    setFromDate('');
    setFromTime('');
    setFromPeriod('AM');
    setToDate('');
    setToTime('');
    setToPeriod('AM');
    setCommentsFilter({ from: null, to: null });
    onHideMyAnnotationsChange(false);
    onHideOthersAnnotationsChange(false);
  }, [setCommentsFilter, onHideMyAnnotationsChange, onHideOthersAnnotationsChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && canSubmit) {
        e.preventDefault();
        handleApply();
      }
    },
    [canSubmit, handleApply],
  );

  return (
    <div className="rt-comments-filter" onKeyDown={handleKeyDown}>
      <div className="rt-comments-filter__header">
        <span className="rt-comments-filter__title">
          {T.translate('filters.filters', { defaultValue: 'Filters' })}
        </span>
        <button
          type="button"
          className="rt-comments-filter__clear-btn"
          onClick={handleClear}
        >
          {T.translate('filters.clear', { defaultValue: 'Clear' })}
        </button>
      </div>

      <div className="rt-comments-filter__section">
        <FormControlLabel
          className="rt-comments-filter__toggle"
          control={
            <Switch
              size="small"
              checked={hideOthersAnnotations}
              onChange={(e) => onHideOthersAnnotationsChange(e.target.checked)}
            />
          }
          label="Hide annotations from other users"
        />
        <FormControlLabel
          className="rt-comments-filter__toggle"
          control={
            <Switch
              size="small"
              checked={hideMyAnnotations}
              onChange={(e) => onHideMyAnnotationsChange(e.target.checked)}
            />
          }
          label="Hide my annotations"
        />
      </div>

      <div className="rt-comments-filter__divider" />

      <div className="rt-comments-filter__section">
        <label className="rt-comments-filter__label">
          {T.translate('generic.from', { defaultValue: 'From' })}
        </label>
        <div className="rt-comments-filter__row">
          <input
            type="text"
            className={`rt-comments-filter__input ${!fromDateValid ? 'is-invalid' : ''}`}
            placeholder="DD/MM/YYYY"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <input
            type="text"
            className={`rt-comments-filter__input rt-comments-filter__input--time ${!fromTimeValid ? 'is-invalid' : ''}`}
            placeholder="hh:mm"
            value={fromTime}
            onChange={(e) => setFromTime(e.target.value)}
          />
          <button
            type="button"
            className="rt-comments-filter__period-toggle"
            onClick={() => setFromPeriod((p) => (p === 'AM' ? 'PM' : 'AM'))}
          >
            <span className={`rt-comments-filter__period-option ${fromPeriod === 'AM' ? 'is-active' : ''}`}>AM</span>
            <span className={`rt-comments-filter__period-option ${fromPeriod === 'PM' ? 'is-active' : ''}`}>PM</span>
          </button>
        </div>
      </div>

      <div className="rt-comments-filter__section">
        <label className="rt-comments-filter__label">
          {T.translate('generic.to', { defaultValue: 'To' })}
        </label>
        <div className="rt-comments-filter__row">
          <input
            type="text"
            className={`rt-comments-filter__input ${!toDateValid ? 'is-invalid' : ''}`}
            placeholder="DD/MM/YYYY"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <input
            type="text"
            className={`rt-comments-filter__input rt-comments-filter__input--time ${!toTimeValid ? 'is-invalid' : ''}`}
            placeholder="hh:mm"
            value={toTime}
            onChange={(e) => setToTime(e.target.value)}
          />
          <button
            type="button"
            className="rt-comments-filter__period-toggle"
            onClick={() => setToPeriod((p) => (p === 'AM' ? 'PM' : 'AM'))}
          >
            <span className={`rt-comments-filter__period-option ${toPeriod === 'AM' ? 'is-active' : ''}`}>AM</span>
            <span className={`rt-comments-filter__period-option ${toPeriod === 'PM' ? 'is-active' : ''}`}>PM</span>
          </button>
        </div>
      </div>

      <div className="rt-comments-filter__actions">
        <button
          type="button"
          className="rt-comments-filter__apply-btn"
          disabled={!canSubmit}
          onClick={handleApply}
        >
          {T.translate('generic.apply', { defaultValue: 'Apply' })}
        </button>
      </div>
    </div>
  );
};

export default React.memo(FilterCommentsPanel);
